// ============================================================
// FORECAST TYPES
// Shared type definitions for the entire forecasting pipeline.
// All engines and UI components reference these types.
// ============================================================

/**
 * A function that accepts a day index `t` (0 = today, 1 = tomorrow, …)
 * and returns a growth/decay multiplier (≥ 0).
 * Used by non-paid revenue evolution models.
 */
export type EvolutionFn = (t: number) => number;

/**
 * Configuration for a non-paid traffic source's evolution model.
 * Includes the growth/decay function, display label, and chart color.
 */
export interface EvolutionConfig {
    fn: EvolutionFn;
    label: string;
    color: string;
}

// ============================================================
// DATA QUALITY & JITTER PROFILING
// ============================================================

/** Tier labels for data quality classification */
export type DataQuality = 'excellent' | 'good' | 'fair' | 'low' | 'stochastic';

/**
 * JitterProfile — Adaptive noise profiling result.
 *
 * Computed per data source from its historical series.
 * Encapsulates all parameters needed for noise injection,
 * confidence band sizing, and UI slider defaults.
 */
export interface JitterProfile {
    /** Multiplier for noise amplitude (replaces fixed 1.155). Higher = more noisy forecast line. */
    noiseMultiplier: number;

    /** Multiplier for confidence band width. Higher = wider bands. */
    bandWidthMultiplier: number;

    /** Recommended default for the noise slider (0-200 scale, 100 = "as-is"). */
    defaultSliderValue: number;

    /** Human-readable label for the UI */
    qualityLabel: DataQuality;

    /** Short description of why this profile was chosen */
    qualityReason: string;

    /** Whether to use impulse-style noise (sparse/stochastic sources) */
    useImpulseNoise: boolean;

    /** Impulse probability (only relevant if useImpulseNoise) */
    impulseProbability: number;

    /** The computed effective sigma (std) to use for noise generation */
    effectiveSigma: number;

    /**
     * Relative coefficient of variation: noise expressed as fraction of signal level.
     * This is the key field for multiplicative noise: noise = base * relativeCV * random
     */
    relativeCV: number;

    /**
     * Data-driven default for the optimism slider (0-100 scale, 50 = neutral).
     * Based on trend direction, momentum, consistency, and staleness.
     */
    defaultOptimism: number;
}

// ============================================================
// ECOMMERCE PHASES
// ============================================================

/** 
 * MarketingPhase — Represents the current state of an ad entity in its lifecycle.
 */
export type MarketingPhase = 'testing' | 'scaling' | 'stabilizing' | 'sunset' | 'learning' | 'paused';

/**
 * PhaseInfo — Contains the detected phase and the reasoning/confidence behind it.
 */
export interface PhaseInfo {
    current: MarketingPhase;
    reason: string;
    confidence: number; // 0.0 to 1.0
}

// ============================================================
// ROAS CHART DATA POINT
// ============================================================

/**
 * RoasPoint — A single data point in a ROAS forecast chart.
 *
 * Each point represents one day and contains:
 *   - Historical actuals (if available)
 *   - Forecast baseline + noise + confidence band (if in forecast zone)
 */
export type RoasPoint = {
    /** Display label (e.g. "Mar 5") */
    label: string;

    /** ISO date string for robust matching/projection */
    date?: string;

    /** Actual observed ROAS for this day (null if in forecast zone) */
    actualRoas: number | null;

    /** Actual ad spend for this day (null if in forecast zone). Used for revenue derivation. */
    actualSpend: number | null;

    /** Actual revenue for this day (null if in forecast zone). */
    actualRevenue: number | null;

    /** The "ideal" smooth forecast value from DES */
    baselineRoas: number | null;

    /** The sampled jitter from historical sigma (for noise injection) */
    stochasticNoise: number | null;

    /** Upper confidence bound */
    upperRoas: number | null;

    /** Lower confidence bound */
    lowerRoas: number | null;
};

// ============================================================
// NON-PAID CHART DATA POINT
// ============================================================

/**
 * NonPaidPoint — A single data point in a non-paid revenue forecast chart.
 *
 * Same structure as RoasPoint but uses absolute revenue values
 * instead of ROAS ratios.
 */
export type NonPaidPoint = {
    /** Display label (e.g. "Mar 5") */
    label: string;

    /** ISO date string for robust matching/projection */
    date?: string;

    /** Actual observed revenue for this day (null if in forecast zone) */
    actual: number | null;

    /** Smooth forecast baseline from DES + evolution model */
    baseline: number | null;

    /** Sampled noise for this forecast point */
    stochasticNoise: number | null;

    /** Upper confidence bound */
    upper: number | null;

    /** Lower confidence bound */
    lower: number | null;
};

// ============================================================
// GENERIC FORECAST CHART POINT
// ============================================================

/**
 * ForecastChartPoint — A generic data point for aggregated charts.
 */
export interface ForecastChartPoint {
    label: string;
    date: string;
    actual: number | null;
    forecast: number | null;
    upper: number | null;
    lower: number | null;
    isForecast: boolean;
    netProfit?: number | null;
    actualNetProfit?: number | null;
    forecastNetProfit?: number | null;
    netProfitUpper?: number | null;
    netProfitLower?: number | null;
}

// ============================================================
// ROAS FORECAST OUTPUT (per source)
// ============================================================

/**
 * RoasSourceForecast — Complete forecast output for a single ROAS source (e.g. "meta").
 *
 * Contains the chart-ready data array, model metadata, and the jitter profile.
 * This is one "building block" in the ROAS forecast pipeline.
 */
export interface RoasSourceForecast {
    /** Unique entity ID */
    id: string;

    /** Platform/source name (e.g. "meta", "tiktok") */
    platform: string;

    /** Ad/Entity name or ID */
    source: string;

    /** Chart line color */
    color: string;

    /** Chart-ready data array (historical + forecast points) */
    data: RoasPoint[];

    /** Ad fatigue half-life in days */
    halfLife: number;

    /** How the half-life was determined: 'data-driven' or 'platform-default' */
    halfLifeSource: string;

    /** Backtest accuracy score (0-100), or null if insufficient data */
    backtestScore: number | null;

    /** Average recent daily spend (last 14 days with spend > 0) */
    avgSpend: number;

    /** Adaptive jitter profile computed from this source's historical data */
    jitterProfile: JitterProfile;

    /** The detected lifecycle phase of this ad */
    phase: PhaseInfo;
}

/**
 * RoasForecastResult — The complete output of the ROAS forecast engine.
 *
 * Contains:
 *   - perSourceCharts: Individual forecast per ad platform
 *   - combinedChart: Spend-weighted blended ROAS
 *   - backtestScores: Per-source accuracy scores
 *   - jitterProfiles: Per-source noise profiles (for UI sliders)
 */
export interface RoasForecastResult {
    perSourceCharts: RoasSourceForecast[];
    combinedChart: RoasPoint[];
    backtestScores: Record<string, number>;
    jitterProfiles: Record<string, JitterProfile>;
}

// ============================================================
// NON-PAID FORECAST OUTPUT (per source)
// ============================================================

/**
 * NonPaidSourceForecast — Complete forecast output for a single non-paid source.
 */
export interface NonPaidSourceForecast {
    /** Source display name (e.g. "Direct / Organic", "Facebook") */
    source: string;

    /** Chart line color */
    color: string;

    /** Evolution model label (e.g. "Organic Facebook", "SEO / Organic Search") */
    evolutionLabel: string;

    /** Chart-ready data array (historical + forecast points) */
    data: NonPaidPoint[];

    /** Backtest accuracy score (0-100), or null if insufficient data */
    backtestScore: number | null;

    /** Adaptive jitter profile */
    jitterProfile: JitterProfile;
}

/**
 * NonPaidForecastResult — The complete output of the non-paid forecast engine.
 */
export interface NonPaidForecastResult {
    charts: NonPaidSourceForecast[];
    npJitterProfiles: Record<string, JitterProfile>;
}

// ============================================================
// EXPENSE FORECAST DATA POINT
// ============================================================

/**
 * ExpensePoint — A single data point in an expense forecast chart.
 */
export interface ExpensePoint {
    label: string;
    date: string;
    actual: number | null;
    forecast: number | null;
    upper: number | null;
    lower: number | null;
    isForecast: boolean;
}

/**
 * ExpenseSourceForecast — Forecast for a specific expense source (e.g., "Meta Ads", "Taxes (Spec)").
 */
export interface ExpenseSourceForecast {
    sourceId: string;
    sourceName: string;
    type: 'marketing' | 'journal' | 'spec' | 'general';
    color: string;
    data: ExpensePoint[];
}

/**
 * ExpenseForecastResult — Aggregated result of the expense forecast engine.
 */
export interface ExpenseForecastResult {
    sources: ExpenseSourceForecast[];
    combinedChart: ExpensePoint[];
    totalHistorical: number;
    totalForecasted: number;
}

// ============================================================
// JOURNAL OPERATIONS
// ============================================================

/**
 * JournalOperation — A discrete transaction or recurring entry from a money journal.
 */
export interface JournalOperation {
    id: string;
    journalId: string;
    type: 'income' | 'expense';
    amount: number;
    date?: string; // Pinpointed
    frequency?: 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly'; // Recurring
    isActive?: boolean;
    startDate?: string;
    endDate?: string;
    label: string;
}

// ============================================================
// RAW FORECAST POINT (internal intermediate form)
// ============================================================

/**
 * RawForecastPoint — A single forecast point before chart formatting.
 *
 * This is the raw output from the DES forecast generator.
 * It contains the baseline, noise, and confidence band values
 * before they are formatted into RoasPoint or NonPaidPoint.
 */
export interface RawForecastPoint {
    /** Smooth baseline value (DES + seasonality + fatigue/evolution) */
    baseline: number;

    /** Sampled noise for this point */
    noise: number;

    /** Upper confidence bound */
    upper: number;

    /** Lower confidence bound */
    lower: number;
}

// ============================================================
// QUALITY BADGE STYLING (for UI)
// ============================================================

export const QUALITY_COLORS: Record<DataQuality, { bg: string; text: string; darkBg: string; darkText: string }> = {
    excellent: { bg: 'bg-emerald-100', text: 'text-emerald-700', darkBg: 'dark:bg-emerald-900/30', darkText: 'dark:text-emerald-400' },
    good: { bg: 'bg-sky-100', text: 'text-sky-700', darkBg: 'dark:bg-sky-900/30', darkText: 'dark:text-sky-400' },
    fair: { bg: 'bg-amber-100', text: 'text-amber-700', darkBg: 'dark:bg-amber-900/30', darkText: 'dark:text-amber-400' },
    low: { bg: 'bg-red-100', text: 'text-red-700', darkBg: 'dark:bg-red-900/30', darkText: 'dark:text-red-400' },
    stochastic: { bg: 'bg-purple-100', text: 'text-purple-700', darkBg: 'dark:bg-purple-900/30', darkText: 'dark:text-purple-400' },
};
